import { ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import Pill, { PillTone } from './ui/Pill';
import Button from './ui/Button';
import MoreMenu from './ui/MoreMenu';
import { Panel, PanelHeader } from './ui/Panel';
import { LoadingPanel, ErrorNotice, EmptyState } from './ui/States';
import { errorMessage } from '../lib/printable';
import type {
  ChecklistCompleteRequest,
  ChecklistGroup,
  ChecklistItem,
  ChecklistReasonRequest,
  ChecklistState,
  DataResponse,
} from '../contracts';

export type StepState = ChecklistState;
export type { ChecklistItem, ChecklistGroup };

type Action = 'complete' | 'skip' | 'not-applicable';

export type EvidenceFields = Record<string, { key: string; label: string; required?: boolean }[]>;

const TZ = 'America/Toronto';
const SOON_DAYS = 14;
const DONE_SHOWN = 3;
const STATE: Record<StepState, { label: string; tone: PillTone }> = {
  blocked: { label: 'Waiting', tone: 'inactive' },
  open: { label: 'Open', tone: 'planned' },
  done: { label: 'Done', tone: 'done' },
  skipped: { label: 'Skipped', tone: 'inactive' },
  not_applicable: { label: 'Not applicable', tone: 'inactive' },
};
const OWNER: Record<string, string> = {
  program_manager: 'Program Manager',
  npi: 'NPI',
  supervisor: 'Supervisor',
  speaker: 'Speaker',
  research_services: 'Research Services',
  platform: 'Platform',
};
const CLOSED: StepState[] = ['done', 'skipped', 'not_applicable'];

const day = (v: string) =>
  new Date(v).toLocaleDateString('en-CA', { timeZone: TZ, year: 'numeric', month: 'short', day: 'numeric' });
/** "Mar 5", with the year only when it isn't this year. */
const shortDay = (v: string) => {
  const d = new Date(v);
  const year = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: TZ, year: 'numeric' });
  return d.toLocaleDateString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    ...(year(d) === year(new Date()) ? {} : { year: 'numeric' }),
  });
};

function evidenceText(e: Record<string, unknown>) {
  return Object.entries(e)
    .filter(([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object')
    .map(([k, v]) =>
      k.endsWith('Cents') && typeof v === 'number'
        ? `${k
            .slice(0, -5)
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase()}: ${(v / 100).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })}`
        : `${k.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${String(v)}`,
    )
    .join(' · ');
}

export function ProgressBar({ finished, total, overdue = 0 }: { finished: number; total: number; overdue?: number }) {
  const pct = total ? Math.round((finished / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="h-[6px] flex-1 bg-line rounded-sm" aria-label={`${finished} of ${total} steps finished`}>
        <div className="h-full bg-accent rounded-sm" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-small text-ink-2 tabular whitespace-nowrap">
        {finished}/{total}
        {overdue ? ` · ${overdue} late` : ''}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- grouping (the event Timeline)

const time = (v: string | null | undefined) => (v ? new Date(v).getTime() : Infinity);
const byDue = (a: ChecklistItem, b: ChecklistItem) => time(a.dueAt) - time(b.dueAt) || a.position - b.position;

/** Splits a checklist into what needs attention: past due, due soon, later, waiting, done, skipped or N/A. */
export function timelineGroups(items: ChecklistItem[], now = new Date()) {
  const cut = now.getTime() + SOON_DAYS * 24 * 3600e3;
  const open = items.filter((i) => i.state === 'open');
  return {
    past: open.filter((i) => i.overdue).sort(byDue),
    soon: open.filter((i) => !i.overdue && i.dueAt && time(i.dueAt) <= cut).sort(byDue),
    later: open.filter((i) => !i.overdue && (!i.dueAt || time(i.dueAt) > cut)).sort(byDue),
    waiting: items.filter((i) => i.state === 'blocked').sort((a, b) => a.position - b.position),
    done: items
      .filter((i) => i.state === 'done')
      .sort((a, b) => time(b.completedAt) - time(a.completedAt) || b.position - a.position),
    other: items.filter((i) => i.state === 'skipped' || i.state === 'not_applicable'),
  };
}

/** The figures for the Timeline's opening sentence, across every checklist on the subject. */
export function timelineCounts(groups: ChecklistGroup[], now = new Date()) {
  const items = groups.flatMap((g) => g.items);
  const t = timelineGroups(items, now);
  return {
    total: groups.reduce((n, g) => n + g.progress.total, 0),
    done: t.done.length,
    skipped: items.filter((i) => i.state === 'skipped').length,
    late: t.past.length,
    soon: t.soon.length,
  };
}

export const checklistKey = (subjectType: string, subjectId: string) => ['checklist', subjectType, subjectId];

export function useChecklist(subjectType: string, subjectId: string) {
  return useQuery<ChecklistGroup[]>(
    checklistKey(subjectType, subjectId),
    async () =>
      (await api.get<DataResponse<ChecklistGroup[]>>('/checklists', { params: { subjectType, subjectId } })).data.data,
  );
}

// ---------------------------------------------------------------- tables

// Rows stack below 768px: header hidden, each cell on its own line after its column name.
const TABLE = 'w-full border-collapse tabular max-md:block';
const HEAD = 'text-left text-small text-ink-2 border-b border-line-strong max-md:hidden';
const TH = 'py-2 pr-3 last:pr-0 align-bottom font-semibold';
const ROW = 'border-b border-line hover:bg-selected max-md:block max-md:py-3';
const TD = 'py-[11px] pr-3 last:pr-0 align-top text-[15px] leading-[22px] text-ink max-md:block max-md:p-0';
const LABELLED =
  "max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-ink-3 max-md:before:text-small max-md:before:font-normal";
const EM = 'block text-small text-ink-3';

/** A subject's checklists (steps, states, due dates) with operator actions for admins. `grouped` is the event
 *  Timeline's layout (past due, due soon, waiting, done); `flat` (default) is one table in checklist order. */
export default function Checklist({
  subjectType,
  subjectId,
  mutable,
  onChange,
  evidenceFields = {},
  layout = 'flat',
}: {
  subjectType: string;
  subjectId: string;
  mutable: boolean;
  onChange?: () => void;
  evidenceFields?: EvidenceFields;
  layout?: 'flat' | 'grouped';
}) {
  const queryClient = useQueryClient();
  const key = checklistKey(subjectType, subjectId);
  const [pending, setPending] = useState<{ id: string; action: Action } | null>(null);
  const [text, setText] = useState('');
  const [showNa, setShowNa] = useState(false);
  const [showLater, setShowLater] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [extra, setExtra] = useState<Record<string, string>>({});

  const list = useChecklist(subjectType, subjectId);
  const act = useMutation(
    async ({
      id,
      action,
      value,
      evidence,
    }: {
      id: string;
      action: Action | 'reopen';
      value?: string;
      evidence?: Record<string, string>;
    }) => {
      const body: ChecklistCompleteRequest | ChecklistReasonRequest | Record<string, never> =
        action === 'complete'
          ? { note: value || undefined, evidence }
          : action === 'reopen'
            ? {}
            : { reason: value ?? '' };
      return (await api.post(`/checklists/items/${id}/${action}`, body)).data.data;
    },
    {
      onSuccess: () => {
        setPending(null);
        setText('');
        setExtra({});
        queryClient.invalidateQueries(key);
        onChange?.();
      },
    },
  );

  if (list.isLoading) return <LoadingPanel label="Loading the checklist…" height={120} />;
  if (list.isError) return <ErrorNotice>Could not load the checklist.</ErrorNotice>;
  if (!list.data?.length) return <EmptyState title="No checklist">No checklist is attached yet.</EmptyState>;

  const start = (id: string, action: Action) => {
    setPending({ id, action });
    setText('');
    setExtra({});
    act.reset();
  };

  /** The note and evidence form under a step, as Done, Skip or Not applicable asks for it. */
  const form = (i: ChecklistItem) => {
    if (pending?.id !== i.id) return null;
    const fields = pending.action === 'complete' ? (evidenceFields[i.stepKey] ?? []) : [];
    const missing = fields.some((f) => f.required && !extra[f.key]?.trim());
    const reason = pending.action !== 'complete';
    const field =
      'block mt-1 min-h-[40px] w-full border border-line-strong rounded-sm bg-surface px-3 py-2 text-[15px] leading-[22px] text-ink';
    return (
      <div className="flex flex-wrap items-end gap-3 mt-3 mb-1">
        {fields.map((f) => (
          <label key={f.key} className="block w-[190px] max-md:w-full text-small font-semibold text-ink-2">
            {f.label}
            {f.required ? '' : ' (optional)'}
            <input
              value={extra[f.key] ?? ''}
              onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
              className={`${field} font-normal`}
            />
          </label>
        ))}
        <label className="block flex-[1_1_220px] text-small font-semibold text-ink-2">
          {reason ? 'Reason' : 'Note (optional)'}
          <input autoFocus value={text} onChange={(e) => setText(e.target.value)} className={`${field} font-normal`} />
        </label>
        <span className="flex gap-3 items-center">
          <Button
            size="sm"
            variant="primary"
            disabled={act.isLoading || missing || (reason && !text.trim())}
            onClick={() =>
              act.mutate({
                id: i.id,
                action: pending.action,
                value: text.trim(),
                evidence: fields.length
                  ? Object.fromEntries(
                      fields.filter((f) => extra[f.key]?.trim()).map((f) => [f.key, extra[f.key].trim()]),
                    )
                  : undefined,
              })
            }
          >
            {pending.action === 'complete' ? 'Mark done' : pending.action === 'skip' ? 'Skip' : 'Not applicable'}
          </Button>
          <Button size="sm" variant="quiet" onClick={() => setPending(null)}>
            Cancel
          </Button>
        </span>
      </div>
    );
  };

  /** Lines under the step: owner when not the Program Manager, then how it closes or how it was closed. */
  const notes = (i: ChecklistItem, grouped: boolean) => {
    const closed = CLOSED.includes(i.state);
    const how = closed
      ? [
          grouped && i.state !== 'done' ? STATE[i.state].label : null,
          i.completedVia === 'automatic' ? 'By the platform' : null,
          i.note,
          evidenceText(i.evidence || {}),
        ]
          .filter(Boolean)
          .join(' · ')
      : i.completion === 'automatic'
        ? 'Closes itself when the platform sees it'
        : '';
    const owner = grouped && i.owner !== 'program_manager' ? `Owner · ${OWNER[i.owner] ?? i.owner}` : '';
    return (
      <>
        {owner ? <span className={EM}>{owner}</span> : null}
        {how ? <span className={EM}>{how}</span> : null}
      </>
    );
  };

  const actions = (i: ChecklistItem) => {
    if (!mutable || pending?.id === i.id) return null;
    if (CLOSED.includes(i.state))
      return (
        <Button
          size="sm"
          variant="quiet"
          disabled={act.isLoading}
          onClick={() => act.mutate({ id: i.id, action: 'reopen' })}
        >
          Reopen
        </Button>
      );
    return (
      <span className="inline-flex gap-2 items-center">
        {i.state === 'open' ? (
          <Button size="sm" onClick={() => start(i.id, 'complete')}>
            Done
          </Button>
        ) : null}
        <MoreMenu
          label={`Skip or not applicable: ${i.label}`}
          items={[
            { label: 'Skip', onClick: () => start(i.id, 'skip') },
            { label: 'Not applicable', onClick: () => start(i.id, 'not-applicable') },
          ]}
        />
      </span>
    );
  };

  const error = act.isError ? (
    <div className="mb-4">
      <ErrorNotice>{errorMessage(act.error, 'The step could not be updated.')}</ErrorNotice>
    </div>
  ) : null;

  if (layout === 'grouped') {
    const many = list.data.length > 1;
    const table = (rows: ChecklistItem[], dateHead: string, closedDates = false) => (
      <table className={`${TABLE} md:table-fixed`}>
        <colgroup>
          <col />
          <col className="w-[120px]" />
          <col className="w-[180px]" />
        </colgroup>
        <thead className={HEAD}>
          <tr>
            <th className={TH}>Step</th>
            <th className={`${TH} text-right`}>{dateHead}</th>
            <th className={TH}>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="max-md:block">
          {rows.map((i) => {
            const at = closedDates ? i.completedAt : i.dueAt;
            return (
              <tr key={i.id} className={ROW}>
                <td className={TD}>
                  <span className={CLOSED.includes(i.state) && i.state !== 'done' ? 'text-ink-2' : ''}>{i.label}</span>
                  {notes(i, true)}
                  {form(i)}
                </td>
                <td
                  data-label={dateHead}
                  className={`${TD} ${LABELLED} md:text-right whitespace-nowrap ${
                    at
                      ? i.overdue
                        ? 'font-semibold'
                        : closedDates || i.state === 'blocked'
                          ? 'text-ink-2'
                          : ''
                      : 'text-ink-3'
                  }`}
                >
                  {at ? shortDay(at) : '–'}
                </td>
                <td className={`${TD} md:text-right whitespace-nowrap max-md:pt-2`}>{actions(i)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
    const more = (shown: boolean, onClick: () => void, label: string) => (
      <p className="mt-3">
        <button
          type="button"
          onClick={onClick}
          className="text-[15px] leading-[22px] text-accent underline underline-offset-[3px] hover:text-ink"
          aria-expanded={shown}
        >
          {label}
        </button>
      </p>
    );
    const section = (title: string, rows: number, body: ReactNode) =>
      rows ? (
        <Panel className="mt-14 max-md:mt-10">
          <PanelHeader title={title} note={String(rows)} />
          {body}
        </Panel>
      ) : null;
    return (
      <div>
        {error}
        {list.data.map((g) => {
          const t = timelineGroups(g.items);
          const name = (s: string) => (many ? `${g.label} · ${s.charAt(0).toLowerCase()}${s.slice(1)}` : s);
          const done = showDone ? t.done : t.done.slice(0, DONE_SHOWN);
          return (
            <div key={g.templateKey}>
              {section(name('Past their due date'), t.past.length, table(t.past, 'Due'))}
              {section(
                name(t.soon.length ? 'Due in the next two weeks' : 'Still to do'),
                t.soon.length || t.later.length,
                <>
                  {t.soon.length ? table(t.soon, 'Due') : null}
                  {t.later.length && (showLater || !t.soon.length) ? (
                    <div className={t.soon.length ? 'mt-6' : ''}>{table(t.later, 'Due')}</div>
                  ) : null}
                  {t.later.length && t.soon.length
                    ? more(
                        showLater,
                        () => setShowLater(!showLater),
                        showLater
                          ? 'Hide the later steps'
                          : `Show the ${t.later.length} later step${t.later.length === 1 ? '' : 's'}`,
                      )
                    : null}
                </>,
              )}
              {section(
                name('Waiting on another step'),
                t.waiting.length,
                <>
                  {table(t.waiting, 'Due')}
                  <p className="text-small text-ink-3 mt-3">
                    These open once the step before them is done. Skip and not applicable both ask for a reason.
                  </p>
                </>,
              )}
              {t.done.length || t.other.length ? (
                <Panel className="mt-14 max-md:mt-10">
                  <PanelHeader title={name('Done')} note={String(t.done.length)} />
                  {done.length ? table(done, 'Done on', true) : null}
                  {t.done.length > DONE_SHOWN
                    ? more(
                        showDone,
                        () => setShowDone(!showDone),
                        showDone ? 'Show only the latest' : `Show the other ${t.done.length - DONE_SHOWN}`,
                      )
                    : null}
                  {t.other.length
                    ? more(
                        showNa,
                        () => setShowNa(!showNa),
                        `${showNa ? 'Hide' : 'Show'} ${t.other.length} skipped or not applicable`,
                      )
                    : null}
                  {showNa && t.other.length ? <div className="mt-3">{table(t.other, 'Closed on', true)}</div> : null}
                </Panel>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error}
      {list.data.map((g) => {
        const na = g.items.filter((i) => i.state === 'not_applicable').length;
        return (
          <div key={g.templateKey}>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-2">
              <div className="font-semibold text-[15px] leading-[22px] text-ink">{g.label}</div>
              <div className="w-[220px]">
                <ProgressBar finished={g.progress.finished} total={g.progress.total} overdue={g.overdue} />
              </div>
            </div>
            <table className={TABLE}>
              <thead className={HEAD}>
                <tr>
                  <th className={TH}>Step</th>
                  <th className={TH}>Owner</th>
                  <th className={`${TH} text-right`}>Due</th>
                  <th className={`${TH} md:pl-4`}>State</th>
                  <th className={TH}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="max-md:block">
                {g.items
                  .filter((i) => showNa || i.state !== 'not_applicable')
                  .map((i) => {
                    const closed = CLOSED.includes(i.state);
                    const at = closed && i.completedAt ? i.completedAt : i.dueAt;
                    return (
                      <tr key={i.id} className={ROW}>
                        <td className={TD}>
                          <span className={closed && i.state !== 'done' ? 'text-ink-2' : ''}>{i.label}</span>
                          {notes(i, false)}
                          {form(i)}
                        </td>
                        <td data-label="Owner" className={`${TD} ${LABELLED} whitespace-nowrap text-ink-2`}>
                          {OWNER[i.owner] ?? i.owner}
                        </td>
                        <td
                          data-label={closed ? 'Closed' : 'Due'}
                          className={`${TD} ${LABELLED} md:text-right whitespace-nowrap ${
                            i.overdue ? 'font-semibold' : at ? 'text-ink-2' : 'text-ink-3'
                          }`}
                        >
                          {at ? day(at) : '–'}
                        </td>
                        <td className={`${TD} whitespace-nowrap md:pl-4`}>
                          {i.overdue ? (
                            <span className="text-ink-2">Past its date</span>
                          ) : (
                            <Pill tone={STATE[i.state].tone}>{STATE[i.state].label}</Pill>
                          )}
                        </td>
                        <td className={`${TD} md:text-right whitespace-nowrap max-md:pt-2`}>{actions(i)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {na ? (
              <p className="mt-3">
                <button
                  type="button"
                  className="text-[15px] leading-[22px] text-accent underline underline-offset-[3px] hover:text-ink"
                  onClick={() => setShowNa(!showNa)}
                >
                  {showNa ? 'Hide' : 'Show'} {na} step{na === 1 ? '' : 's'} marked not applicable
                </button>
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
